/*
 * Copyright (C) 2020 to Present Applause App Quality, Inc. All rights reserved.
 */

import { ChangeDetectionStrategy, ChangeDetectorRef, Component, forwardRef, Input, OnDestroy, OnInit, Optional, Self } from '@angular/core';
import { ControlValueAccessor, NgControl, Validators } from '@angular/forms';
import { BehaviorSubject, combineLatest, Observable, Subject } from 'rxjs';
import { distinctUntilChanged, map, takeUntil } from 'rxjs/operators';
import { DisablableCheckbox } from './disablable-checkbox';

@Component({
  selector: 'ngf-checkbox',
  templateUrl: './checkbox.component.html',
  styleUrls: ['./checkbox.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: DisablableCheckbox,
      useExisting: forwardRef(() => CheckboxComponent)
    }
  ],
  standalone: false
})
export class CheckboxComponent implements OnInit, OnDestroy, ControlValueAccessor, DisablableCheckbox {

  private readonly readonly = new BehaviorSubject<boolean>(false);

  @Input()
  value: any | undefined;

  @Input()
  ariaDescribedby?: string;

  @Input()
  ariaRequired?: boolean;

  // eslint-disable-next-line @angular-eslint/no-input-rename
  @Input('readonly')
  set isReadonly(isReadonly: boolean) {
    this.readonly.next(isReadonly);
  }
  get isReadonly() {
    return this.readonly.getValue();
  }

  private onChangeFn: (value: any) => void = () => undefined;
  private onTouchedFn: () => void = () => undefined;
  private isChecked = new BehaviorSubject<boolean>(false);
  isChecked$: Observable<boolean>;

  private readonly disabled = new BehaviorSubject<boolean>(false);
  readonly disabled$ = combineLatest([this.disabled, this.readonly]).pipe(map(([disabled, readonly]) => disabled || readonly), distinctUntilChanged());

  private readonly destroy$ = new Subject<void>();

  constructor(
    @Optional() @Self() private ngControl: NgControl | null,
    private cdr: ChangeDetectorRef
  ) {
    if (ngControl) {
      ngControl.valueAccessor = this;
    }
  }

  ngOnInit() {
    this.isChecked$ = this.isChecked.pipe(distinctUntilChanged());

    const control = this.ngControl?.control;
    if (!control) return;

    control.events
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.cdr.markForCheck());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get computedAriaRequired(): boolean {
    if (this.ariaRequired !== undefined) return this.ariaRequired;
    const control = this.ngControl?.control;
    if (!control) return false;
    return control.hasValidator(Validators.required) || control.hasValidator(Validators.requiredTrue);
  }

  get isInvalid(): boolean {
    const control = this.ngControl?.control;
    return !!(control?.invalid && control?.touched);
  }

  toggle(checked: boolean): void {
    this.isChecked.next(checked);
    this.onTouchedFn();
    this.onChangeFn(this.resolveValue());
  }

  registerOnChange(fn: any): void {
    this.onChangeFn = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouchedFn = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled.next(isDisabled);
  }

  /**
   * if @Input [value] IS NOT set,
   * then param 'value' must be of boolean type, otherwise error is thrown
   *
   * if @Input [value] IS set,
   * then param 'value' will be compared with @input [value] to determinate if checkbox is checked or not
   */
  writeValue(value: any): void {
    if (this.value === undefined) {
      this.isChecked.next(value);
    } else {
      this.isChecked.next(this.value === value);
    }
  }

  /**
   * if @Input [value] IS NOT set, then return boolean value (checkbox state)
   *
   * if @Input [value] IS set, then return:
   *  - @Input [value], if checkbox checked
   *  - null, if checkbox not checked
   */
  private resolveValue(): any {
    if (this.value === undefined) {
      return this.isChecked.value;
    } else {
      return this.isChecked.value
        ? this.value
        : null;
    }
  }
}
